"use client"

import { useState } from "react"
import { useTranslations } from "next-intl"
import { ChevronRight } from "lucide-react"
import { useReducedMotion } from "framer-motion"
import { DockSheet } from "@/components/ui/dock-sheet"
import { DialogCard } from "@/components/ui/dialog-card"
import { useMobile } from "@/hooks/use-mobile"
import { useListingOffers } from "@/hooks/use-offer"
import { CommerceIcons } from "@/lib/icons/commerce-icons"
import { ListingOffersPanel } from "./listing-offers-panel"
import { cn, formatPrice } from "@/lib/utils"

const FEED_MOTION_EASE = [0.4, 0, 0.2, 1] as const
const FEED_MOTION_MS = 0.25

interface ListingOffersTriggerProps {
  listingId: string
  currentUserId: string
  currency?: string | null
  initialPending?: number
  initialHighest?: number | null
  className?: string
  forceInline?: boolean
}

export function ListingOffersTrigger({
  listingId,
  currentUserId,
  currency = null,
  initialPending = 0,
  initialHighest = null,
  className,
  forceInline = false,
}: ListingOffersTriggerProps) {
  const isMobile = useMobile()
  const t = useTranslations("offers.panel")
  const reduceMotion = useReducedMotion()
  const [open, setOpen] = useState(false)

  const aggregate = useListingOffers(listingId, {
    initial: { pendingCount: initialPending, highestPending: initialHighest },
  })
  const pending = aggregate?.pendingCount ?? initialPending
  const highest = aggregate?.highestPending ?? initialHighest
  const OfferIcon = CommerceIcons.offer

  const innerEase = {
    duration: reduceMotion ? 0.01 : FEED_MOTION_MS,
    ease: FEED_MOTION_EASE,
  }

  const panelTitle = t("title")
  const triggerLabel =
    pending > 0 ? t("viewOffersWithCount", { count: pending }) : t("viewOffers")
  const highestLabel =
    highest != null && pending > 0
      ? formatPrice(String(highest), currency ?? undefined)
      : null

  if (forceInline && !isMobile) {
    return (
      <ListingOffersPanel
        listingId={listingId}
        currentUserId={currentUserId}
        currency={currency}
        className={className}
      />
    )
  }

  const trigger = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className={cn(
        "flex w-full items-center gap-3 rounded-2xl bg-muted/70 px-4 py-3.5 text-left transition-colors hover:bg-muted active:bg-muted/90",
        className,
      )}
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-background/80 shadow-sm">
        <OfferIcon className="h-[18px] w-[18px] text-foreground" strokeWidth={2} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-semibold text-foreground">{triggerLabel}</span>
        {highestLabel ? (
          <span className="mt-0.5 block text-[13px] text-muted-foreground">
            {t("highestShort", { amount: highestLabel })}
          </span>
        ) : null}
      </span>
      {pending > 0 ? (
        <span className="shrink-0 rounded-full bg-foreground px-2.5 py-1 text-xs font-bold tabular-nums text-background">
          {pending > 99 ? "99+" : pending}
        </span>
      ) : null}
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
    </button>
  )

  if (isMobile) {
    return (
      <>
        {trigger}
        <DockSheet
          open={open}
          onOpenChange={(o) => setOpen(o)}
          title={panelTitle}
          contentSized
          zIndex={1450}
          contentClassName="pt-0 max-h-[min(78dvh,820px)] overflow-y-auto overflow-x-hidden"
          backdropTransition={innerEase}
          panelTransition={{
            y: {
              type: "tween",
              duration: reduceMotion ? 0.01 : FEED_MOTION_MS,
              ease: FEED_MOTION_EASE,
            },
          }}
        >
          <ListingOffersPanel
            listingId={listingId}
            currentUserId={currentUserId}
            currency={currency}
            offerSurfaceVariant="sheet"
          />
        </DockSheet>
      </>
    )
  }

  return (
    <>
      {trigger}
      <DialogCard
        open={open}
        onOpenChange={setOpen}
        nested
        title={panelTitle}
        panelClassName="max-w-lg max-h-[min(90dvh,720px)]"
      >
        <ListingOffersPanel
          listingId={listingId}
          currentUserId={currentUserId}
          currency={currency}
          offerSurfaceVariant="sheet"
        />
      </DialogCard>
    </>
  )
}
